import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { onValue, ref, set, remove } from 'firebase/database'

import { db } from '../lib/firebase.js'
import { PROD_POINTS, ZONES } from '../leaderboard/prodMapPoints.js'
import { pickPremiumCurrentEventId } from '../lib/pickCurrentEvent.js'

// ── GPS Inject (TEST-ONLY) ────────────────────────────────────────────────────
// Feeds fake/live GPS into tournaments/{tid}/tracking/{teamId} on the STANDALONE
// test DB, so the /tv/scrollmap map + one-dot-per-match / one-green-one-match
// logic can be exercised against the flighted demo field WITHOUT a phone on
// course and WITHOUT touching production. Writes are HARD-GATED to the test DB
// (pbcc-tourney-test): on the production DB this page shows a warning and writes
// nothing. Injected teams are re-written every 5s so they stay live on the map.

const DB_URL = import.meta.env.VITE_FIREBASE_DATABASE_URL || ''
const IS_TEST_DB = /pbcc-tourney-test/.test(DB_URL)
const HEARTBEAT_MS = 5000
const HOLES = Array.from({ length: 18 }, (_, i) => i + 1)

// Real-world coord for a hole/zone (from the production geofence points). Tee &
// green are exact; fairway is a point ~12% toward the green (outside the tee fence).
function coordFor(hole, zone) {
  const p = PROD_POINTS[hole]
  if (!p) return null
  if (zone === 'green') return { lat: p.green.lat, lng: p.green.lng }
  if (zone === 'fairway') return { lat: p.tee.lat + (p.green.lat - p.tee.lat) * 0.12, lng: p.tee.lng + (p.green.lng - p.tee.lng) * 0.12 }
  return { lat: p.tee.lat, lng: p.tee.lng }
}

export default function GpsInject() {
  const [tid, setTid] = useState(null)
  const [tname, setTname] = useState('')
  const [teams, setTeams] = useState([])          // [{teamId,name,flightId,opponentId}]
  const [teamId, setTeamId] = useState('')
  const [hole, setHole] = useState(1)
  const [zone, setZone] = useState('green')
  const [injections, setInjections] = useState({}) // {teamId:{lat,lng,teamName,flightId,hole,zone}}
  const [liveTeam, setLiveTeam] = useState('')
  const [status, setStatus] = useState('')
  const injRef = useRef({}); injRef.current = injections
  const liveFixRef = useRef(null)
  const liveTeamRef = useRef(''); liveTeamRef.current = liveTeam
  const teamsRef = useRef([]); teamsRef.current = teams

  // Resolve the active (flighted) tournament + its teams.
  useEffect(() => {
    const off = onValue(ref(db, 'tournaments'), (snap) => {
      const all = snap.val() || {}
      const id = pickPremiumCurrentEventId(all)
      setTid(id)
      const t = all[id]
      setTname((t?.info?.name || '').split('\n')[0])
      const list = []
      for (const [fid, f] of Object.entries(t?.flights || {})) {
        if (fid.startsWith('_')) continue
        for (const [tmId, tm] of Object.entries(f?.teams || {})) {
          list.push({ teamId: tmId, name: tm?.info?.name || tmId, flightId: fid, opponentId: tm?.info?.opponentId || null })
        }
      }
      list.sort((a, b) => (a.flightId < b.flightId ? -1 : a.flightId > b.flightId ? 1 : a.name.localeCompare(b.name)))
      setTeams(list)
      setTeamId((cur) => cur || (list[0]?.teamId ?? ''))
    })
    return () => { try { off() } catch {} }
  }, [])

  const teamById = (id) => teamsRef.current.find((t) => t.teamId === id)

  const place = () => {
    if (!teamId) return
    const c = coordFor(hole, zone)
    if (!c) return
    const t = teamById(teamId)
    setInjections((prev) => ({ ...prev, [teamId]: { ...c, teamName: t?.name || teamId, flightId: t?.flightId || null, hole, zone } }))
    setStatus(`${t?.name || teamId} → H${hole} ${zone}${t?.opponentId ? ' (paired → one match dot)' : ''}`)
  }
  const drop = (id) => {
    setInjections((prev) => { const n = { ...prev }; delete n[id]; return n })
    if (tid) remove(ref(db, `tournaments/${tid}/tracking/${id}`)).catch(() => {})
  }
  const clearAll = () => {
    if (tid) for (const id of Object.keys(injRef.current)) remove(ref(db, `tournaments/${tid}/tracking/${id}`)).catch(() => {})
    setInjections({}); setLiveTeam('')
  }

  // Heartbeat — re-write every injected team every 5s so their dots stay fresh
  // (< the map's 20s stale window). Test DB only.
  useEffect(() => {
    if (!tid || !IS_TEST_DB) return undefined
    const write = () => {
      const now = Date.now()
      for (const [id, v] of Object.entries(injRef.current)) {
        set(ref(db, `tournaments/${tid}/tracking/${id}`), { lat: v.lat, lng: v.lng, teamName: v.teamName, flightId: v.flightId, updatedAt: now }).catch(() => {})
      }
      const lf = liveFixRef.current
      const lt = liveTeamRef.current
      if (lf && lt) {
        const t = teamById(lt)
        set(ref(db, `tournaments/${tid}/tracking/${lt}`), { lat: lf.lat, lng: lf.lng, teamName: t?.name || lt, flightId: t?.flightId || null, updatedAt: now }).catch(() => {})
      }
    }
    write()
    const h = setInterval(write, HEARTBEAT_MS)
    return () => clearInterval(h)
  }, [tid])

  // Optional: broadcast this device's real GPS as the selected live team.
  useEffect(() => {
    if (!liveTeam || typeof navigator === 'undefined' || !navigator.geolocation) return undefined
    const id = navigator.geolocation.watchPosition(
      (p) => { liveFixRef.current = { lat: p.coords.latitude, lng: p.coords.longitude } },
      () => {}, { enableHighAccuracy: true, maximumAge: 0 },
    )
    return () => { try { navigator.geolocation.clearWatch(id) } catch {} }
  }, [liveTeam])

  const injList = Object.entries(injections)

  if (!IS_TEST_DB) {
    return (
      <Shell>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>GPS Inject — disabled on production</h1>
        <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
          This test tool only writes to the standalone test database. Open it on
          <b> pbcc.web.app/gpsinject</b> (the isolated demo copy). Current DB: <code>{DB_URL}</code>
        </p>
        <Link to="/tv/scrollmap" style={BTN}>Go to the map →</Link>
      </Shell>
    )
  }

  return (
    <Shell>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>🧪 GPS Inject <span style={{ fontSize: 12, color: '#FCD34D' }}>TEST DB</span></h1>
        <Link to="/tv/scrollmap" style={BTN}>Watch the map ↗</Link>
      </div>
      <div style={{ fontSize: 12, opacity: 0.8 }}>{tname || '—'} · writes to tournaments/{tid}/tracking · re-writes every 5s</div>

      <div style={CARD}>
        <div style={ROW}>
          <span style={LBL}>Team</span>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)} style={{ ...SELECT, flex: 1 }}>
            {teams.map((t) => <option key={t.teamId} value={t.teamId}>{t.flightId} · {t.name}{t.opponentId ? '' : ' (solo)'}</option>)}
          </select>
        </div>
        <div style={ROW}>
          <span style={LBL}>Hole</span>
          <select value={hole} onChange={(e) => setHole(Number(e.target.value))} style={SELECT}>
            {HOLES.map((h) => <option key={h} value={h}>Hole {h}</option>)}
          </select>
          <span style={LBL}>Zone</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {ZONES.map((z) => <button key={z} onClick={() => setZone(z)} style={{ ...CHIP, ...(z === zone ? CHIP_ON : null) }}>{z}</button>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={place} style={{ ...BTN, background: '#166534', borderColor: '#22C55E' }}>＋ Place team here</button>
          <button onClick={() => setLiveTeam(teamId)} style={BTN} title="Stream THIS device's GPS as the selected team">📡 Use my live GPS</button>
        </div>
        {status ? <div style={{ fontSize: 12, color: '#FCD34D' }}>{status}</div> : null}
      </div>

      <div style={{ ...CARD, gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 800 }}>Live on the map ({injList.length}{liveTeam ? ' + 1 live GPS' : ''})</span>
          {injList.length || liveTeam ? <button onClick={clearAll} style={{ ...BTN, borderColor: '#EF4444', color: '#FCA5A5' }}>Clear all</button> : null}
        </div>
        {liveTeam ? <div style={LIST_ROW}>📡 {teamById(liveTeam)?.name || liveTeam} — your live phone GPS <button onClick={() => setLiveTeam('')} style={XBTN}>✕</button></div> : null}
        {injList.length === 0 && !liveTeam ? <div style={{ opacity: 0.6, fontSize: 12 }}>none yet — pick a team + hole/zone, then Place.</div> : null}
        {injList.map(([id, v]) => (
          <div key={id} style={LIST_ROW}>
            <span style={{ fontWeight: 700 }}>{v.teamName}</span>
            <span style={{ opacity: 0.85 }}>H{v.hole} {v.zone}</span>
            <button onClick={() => drop(id)} style={XBTN}>✕</button>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, opacity: 0.75, lineHeight: 1.5 }}>
        <b>Test one-green-one-match:</b> place two DIFFERENT matches on the same hole’s green — the first owns the green, the second shows at fairway. <b>One dot per match:</b> a paired team shows one dot; placing its opponent too keeps it one dot (freshest). Open <b>/tv/scrollmap</b> to watch.
      </div>
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#123D26', color: '#fff', padding: '20px 16px', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Link to="/teststation" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, textDecoration: 'none' }}>← Test Station</Link>
        {children}
      </div>
    </div>
  )
}

const CARD = { background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }
const ROW = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }
const LBL = { fontSize: 12, fontWeight: 800, opacity: 0.85 }
const SELECT = { padding: '6px 8px', borderRadius: 8, border: '1px solid #ffffff33', background: '#0A1810', color: '#fff', fontSize: 13 }
const CHIP = { padding: '5px 10px', borderRadius: 8, border: '1px solid #ffffff33', background: 'transparent', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }
const CHIP_ON = { background: '#F59E0B', borderColor: '#fff', color: '#1a1a1a' }
const BTN = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', textDecoration: 'none' }
const LIST_ROW = { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', fontSize: 13 }
const XBTN = { marginLeft: 'auto', background: 'none', border: 'none', color: '#FCA5A5', fontWeight: 900, cursor: 'pointer', fontSize: 14 }
